import { useCallback, useMemo } from 'react';
import { useApiClient } from 'hooks/useApiClient';
import { API_ROUTES } from 'utils/constants';
import { Role, CreateRoleRequest, UpdateRoleRequest } from 'types/role';

/**
 * Role API service
 */
export function useRoleService() {
  const apiClient = useApiClient();

  /**
   * Get all roles
   */
  const getAllRoles = useCallback(async (): Promise<Role[]> => {
    return apiClient.get<Role[]>(API_ROUTES.ROLE);
  }, [apiClient]);

  /**
   * Get a single role by ID
   */
  const getRoleById = useCallback(
    async (id: string): Promise<Role> => {
      return apiClient.get<Role>(`${API_ROUTES.ROLE}/${id}`);
    },
    [apiClient],
  );

  /**
   * Create a new role
   */
  const createRole = useCallback(
    async (role: CreateRoleRequest): Promise<Role> => {
      return apiClient.post<Role>(
        API_ROUTES.ROLE,
        role as unknown as Record<string, unknown>,
      );
    },
    [apiClient],
  );

  /**
   * Update a role
   */
  const updateRole = useCallback(
    async (id: string, role: UpdateRoleRequest): Promise<Role> => {
      return apiClient.patch<Role>(
        `${API_ROUTES.ROLE}/${id}`,
        role as unknown as Record<string, unknown>,
      );
    },
    [apiClient],
  );

  /**
   * Delete a role
   */
  const deleteRole = useCallback(
    async (id: string): Promise<void> => {
      await apiClient.delete(`${API_ROUTES.ROLE}/${id}`);
    },
    [apiClient],
  );

  return useMemo(
    () => ({
      getAllRoles,
      getRoleById,
      createRole,
      updateRole,
      deleteRole,
    }),
    [getAllRoles, getRoleById, createRole, updateRole, deleteRole],
  );
}
